import { format } from 'date-fns';
import {
  DATE_INPUT_FORMAT,
  DATETIME_INPUT_FORMAT,
  START_OF_DAY_TIME_VALUES,
  TIME_FILTER_DATE_STRING,
  TIME_FILTER_DATE_VALUES,
  TIME_INPUT_FORMAT,
} from '../../constants';
import { TranslateFn } from '../../hooks/context/useTranslate';
import { Control, DateTimeInputControl } from '../../types/types';
import { parseMinMaxDateTimeValue } from '../parseMinMaxDateTimeValue';
import { getMinMaxDates } from '../managerUtils';

export const checkDateTimeFilterOnSubmit = (
  control: Control | undefined,
  value,
  isBetweenMode: boolean,
  t: TranslateFn
) => {
  if (!control || !value) {
    return null;
  }
  const dateTimeControl = control as DateTimeInputControl;
  if (
    control?.type &&
    ['time', 'date', 'dateTime'].includes(control.type) &&
    (dateTimeControl?.min || dateTimeControl?.max)
  ) {
    const isTimeControl = control.type === 'time';
    let minValue: Date | null = null;
    let maxValue: Date | null = null;
    if (dateTimeControl.min) {
      minValue = parseMinMaxDateTimeValue(dateTimeControl.min, isTimeControl);
    }
    if (dateTimeControl.max) {
      maxValue = parseMinMaxDateTimeValue(dateTimeControl.max, isTimeControl);
    }

    if (isTimeControl) {
      if (isBetweenMode) {
        const splitted = value.split(',', 2);
        if (splitted[0] && splitted[1]) {
          const fromTime = new Date(`${TIME_FILTER_DATE_STRING}${splitted[0]}`);
          const toTime = new Date(`${TIME_FILTER_DATE_STRING}${splitted[1]}`);
          if (fromTime.getTime() && toTime.getTime()) {
            fromTime.setFullYear(...TIME_FILTER_DATE_VALUES);
            toTime.setFullYear(...TIME_FILTER_DATE_VALUES);
            if (minValue && (fromTime < minValue || toTime < minValue)) {
              const readableMinValue = format(minValue, TIME_INPUT_FORMAT);
              return [t('errors.minValue').replace('{0}', readableMinValue)];
            }
            if (maxValue && toTime > maxValue) {
              const readableMinValue = format(maxValue, TIME_INPUT_FORMAT);
              return [t('errors.maxValue').replace('{0}', readableMinValue)];
            }
          } else {
            return [t('errors.invalidBetweenError')];
          }
        } else {
          return [t('errors.invalidBetweenError')];
        }
      } else {
        const time = new Date(`${TIME_FILTER_DATE_STRING}${value}`);
        if (time.getTime()) {
          time.setFullYear(...TIME_FILTER_DATE_VALUES);
          if (minValue && time < minValue) {
            const readableMinValue = format(minValue, TIME_INPUT_FORMAT);
            return [t('errors.minValue').replace('{0}', readableMinValue)];
          }
          if (maxValue && time > maxValue) {
            const readableMinValue = format(maxValue, TIME_INPUT_FORMAT);
            return [t('errors.maxValue').replace('{0}', readableMinValue)];
          }
        } else {
          return [t('errors.invalidTime')];
        }
      }
    } else {
      const hasTime = value.includes('T');
      const { min, max } = getMinMaxDates(minValue, maxValue, hasTime);
      const inputFormat = hasTime ? DATETIME_INPUT_FORMAT : DATE_INPUT_FORMAT;

      if (isBetweenMode) {
        const splitted = value.split(',', 2);
        if (splitted[0] && splitted[1]) {
          const fromDateTime = new Date(splitted[0]);
          const toDateTime = new Date(splitted[1]);
          if (fromDateTime.getTime() && toDateTime.getTime()) {
            if (!hasTime) {
              fromDateTime.setHours(...START_OF_DAY_TIME_VALUES);
              toDateTime.setHours(...START_OF_DAY_TIME_VALUES);
            }
            if (min && (fromDateTime < min || toDateTime < min)) {
              const readableMinValue = format(min, inputFormat);
              return [t('errors.minValue').replace('{0}', readableMinValue)];
            } else if (max && (fromDateTime > max || toDateTime > max)) {
              const readableMaxValue = format(max, inputFormat);
              return [t('errors.maxValue').replace('{0}', readableMaxValue)];
            }
          } else {
            return [t('errors.invalidBetweenError')];
          }
        } else {
          return [t('errors.invalidBetweenError')];
        }
      } else {
        const dateTime = new Date(value);
        if (dateTime.getTime()) {
          if (!hasTime) {
            dateTime.setHours(...START_OF_DAY_TIME_VALUES);
          }
          if (min && dateTime < min) {
            const readableMinValue = format(min, inputFormat);
            return [t('errors.minValue').replace('{0}', readableMinValue)];
          } else if (max && dateTime > max) {
            const readableMaxValue = format(max, inputFormat);
            return [t('errors.maxValue').replace('{0}', readableMaxValue)];
          }
        } else {
          return [t('errors.invalidDate')];
        }
      }
    }
  }
  return null;
};
